import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import axios from "axios";
import { Eye, EyeOff } from "lucide-react";
import InssureLogo from "../../../Components/InssureLogo";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { MAIN_URL, getApiHeaders } from "../../../Configurations/Urls";
import { setClientPreferredOrganizationId } from "../../../Utils/clientActiveOrganization";
import { resolveUserRoleLabel } from "../../../Utils/resolveUserRoleLabel";
import { LOGIN_AUTOCOMPLETE } from "../../../Utils/signupFormAutofill";
import { getDefaultHomePath } from "../../../Utils/defaultHomePath";
import { isStudentUser } from "../../../Utils/isStudentUser";
import { resolveActiveWorkspace, setStoredWorkspace } from "../../../Utils/workspaces";
import { rememberStudentPortalSlug } from "../../../Utils/studentPortalPath";
import { resolveLogoUrl } from "../../EmsOptimized/List/feeReceiptUtils";

const SpinSVG = () => (
  <svg width="18" height="18" viewBox="0 0 15 15" fill="none"
    style={{ animation: "spin 0.8s linear infinite", flexShrink: 0 }}>
    <circle cx="7.5" cy="7.5" r="5.5" stroke="currentColor" strokeOpacity=".3" strokeWidth="2.5" fill="none" />
    <path d="M7.5 2A5.5 5.5 0 0 1 13 7.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none" />
  </svg>
);

function StudentOrgLogin() {
  const { orgSlug } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn, login, logout, setAccess } = useAuthStore();

  const [org, setOrg] = useState(null);
  const [orgError, setOrgError] = useState("");
  const [orgLoading, setOrgLoading] = useState(true);

  const [mode, setMode] = useState("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);

  const rememberKey = (name) =>
    `student_${name}_${String(orgSlug || "").trim().toLowerCase()}`;

  useEffect(() => {
    const savedEmail = localStorage.getItem(rememberKey("email"));
    const savedPassword = localStorage.getItem(rememberKey("password"));
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
    if (savedPassword) {
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, [orgSlug]);

  const persistRememberMe = () => {
    if (rememberMe) {
      localStorage.setItem(rememberKey("email"), email);
      localStorage.setItem(rememberKey("password"), password);
    } else {
      localStorage.removeItem(rememberKey("email"));
      localStorage.removeItem(rememberKey("password"));
    }
  };

  useEffect(() => {
    setLogoFailed(false);
  }, [org?.logo_url]);

  useEffect(() => {
    let cancelled = false;
    const loadOrg = async () => {
      setOrgLoading(true);
      setOrgError("");
      try {
        const response = await axios.get(
          `${MAIN_URL}/api/student-auth/organization/${encodeURIComponent(orgSlug || "")}`,
          { headers: getApiHeaders() }
        );
        if (!cancelled) {
          const nextOrg = response.data?.data || null;
          setOrg(nextOrg);
          rememberStudentPortalSlug(nextOrg?.slug || orgSlug);
        }
      } catch {
        if (!cancelled) {
          setOrg(null);
          setOrgError("Institute not found.");
        }
      } finally {
        if (!cancelled) setOrgLoading(false);
      }
    };
    loadOrg();
    return () => {
      cancelled = true;
    };
  }, [orgSlug]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (isLoggedIn && token) {
      navigate(getDefaultHomePath(useAuthStore.getState().userData), { replace: true });
    } else if (isLoggedIn && !token) {
      logout();
    }
  }, [isLoggedIn, logout, navigate]);

  const finishLogin = async (token, user) => {
    rememberStudentPortalSlug(orgSlug || org?.slug);
    localStorage.setItem("token", token);
    localStorage.removeItem("ems_billing_only");
    const orgId =
      Number(org?.organization_id) ||
      Number(user?.user_active_organization?.organization_id) ||
      Number(user?.organization?.[0]?.organization_id);

    if (orgId) {
      try {
        const response = await axios.post(
          `${MAIN_URL}/api/organizations/extra/active`,
          {
            organization_id: orgId,
            application_user_id: user.application_user_id,
          },
          {
            headers: getApiHeaders({ Authorization: `Bearer ${token}` }),
          }
        );
        const nextUser = response.data?.user || user;
        const allOrgs = Array.isArray(nextUser?.organization)
          ? nextUser.organization
          : Array.isArray(user?.organization)
            ? user.organization
            : [];
        const activeOrg =
          allOrgs.find((item) => Number(item.organization_id) === Number(orgId)) ||
          allOrgs[0] ||
          null;
        setClientPreferredOrganizationId(Number(orgId));
        const sessionUser = {
          ...nextUser,
          organizations: allOrgs,
          organization: activeOrg,
        };
        setStoredWorkspace(resolveActiveWorkspace(sessionUser));
        login(sessionUser);
        setAccess(resolveUserRoleLabel(sessionUser));
        navigate(
          isStudentUser(nextUser) ? "/student/dashboard" : getDefaultHomePath(nextUser),
          { replace: true }
        );
        return;
      } catch {
        /* fall through to local session */
      }
    }

    setStoredWorkspace(resolveActiveWorkspace(user));
    login(user);
    setAccess(resolveUserRoleLabel(user));
    navigate(
      isStudentUser(user) ? "/student/dashboard" : getDefaultHomePath(user),
      { replace: true }
    );
  };

  const sendOtp = async () => {
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setErrors({ email: "Enter a valid email address" });
      return;
    }
    persistRememberMe();
    setLoading(true);
    setErrors({});
    try {
      await axios.post(
        `${MAIN_URL}/api/student-auth/send-otp`,
        { email, slug: orgSlug },
        { headers: getApiHeaders() }
      );
      setOtpSent(true);
      toast.success("If this email is registered, we sent a login code.");
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not send OTP.");
    } finally {
      setLoading(false);
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    const nextErrors = {};
    if (!email) nextErrors.email = "Email is required";
    if (!password || password.length < 6) {
      nextErrors.password = "Password must be at least 6 characters";
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    persistRememberMe();
    setLoading(true);
    try {
      const response = await axios.post(
        `${MAIN_URL}/api/student-auth/login`,
        { email, password, slug: orgSlug },
        { headers: getApiHeaders() }
      );
      toast.success("Login successful!");
      await finishLogin(response.data.token, response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.message || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    const nextErrors = {};
    if (!email) nextErrors.email = "Email is required";
    if (!/^[0-9]{6}$/.test(String(otp || ""))) {
      nextErrors.otp = "Enter the 6-digit code";
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setLoading(true);
    try {
      const response = await axios.post(
        `${MAIN_URL}/api/student-auth/verify-otp`,
        { email, otp, slug: orgSlug },
        { headers: getApiHeaders() }
      );
      toast.success("Login successful!");
      await finishLogin(response.data.token, response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.message || "Invalid or expired code.");
    } finally {
      setLoading(false);
    }
  };

  const logoSrc = !logoFailed ? resolveLogoUrl(org?.logo_url) : "";
  const instituteName = org?.organization_name || "Student portal";
  const initials = String(instituteName)
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "ORG";

  const brandMark = orgLoading ? (
    <div className="ems-auth-org-mark is-loading" />
  ) : logoSrc ? (
    <div className="ems-auth-org-mark">
      <img
        src={logoSrc}
        alt={instituteName}
        className="ems-auth-org-logo"
        onError={() => setLogoFailed(true)}
      />
    </div>
  ) : org ? (
    <div className="ems-auth-org-mark is-fallback" aria-hidden="true">
      {initials}
    </div>
  ) : (
    <InssureLogo className="ems-auth-brand-logo" />
  );

  return (
    <div className="ems-auth-root ems-student-auth">
      <div className="ems-student-brand">
        <div className="ems-student-logo-stage">{brandMark}</div>
        {!orgLoading && !orgError ? (
          <div className="ems-student-brand-copy">
            <p className="ems-student-brand-name">{instituteName}</p>
            <p className="ems-student-brand-tag">Student Portal</p>
          </div>
        ) : null}
        <div className="ems-student-powered ems-student-powered-left">
          <span>Powered by</span>
          <InssureLogo className="ems-student-powered-logo" />
        </div>
      </div>

      <div className="ems-student-form">
        <div className="ems-student-card">
          {orgLoading ? (
            <p className="ems-auth-sub">Loading institute…</p>
          ) : orgError ? (
            <>
              <h1 className="ems-auth-h">Institute not found</h1>
              <p className="ems-auth-sub">
                This student portal link is not valid. Ask your institute for the
                correct login URL.
              </p>
            </>
          ) : (
            <>
              <h1 className="ems-auth-h">Welcome back</h1>
              <p className="ems-auth-sub">Sign in with your student email to continue.</p>

              <div className="ems-auth-tabs">
                <button
                  type="button"
                  className={mode === "password" ? "is-active" : ""}
                  onClick={() => setMode("password")}
                >
                  Password
                </button>
                <button
                  type="button"
                  className={mode === "otp" ? "is-active" : ""}
                  onClick={() => setMode("otp")}
                >
                  Email OTP
                </button>
              </div>

              {mode === "password" ? (
                <form onSubmit={submitPassword} noValidate {...LOGIN_AUTOCOMPLETE.form}>
                  <div className="ems-auth-field">
                    <label htmlFor="stu-email">Email Address</label>
                    <input
                      id="stu-email"
                      type="text"
                      className="ems-fm-input"
                      placeholder="you@email.com"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setErrors((p) => ({ ...p, email: "" }));
                      }}
                      {...LOGIN_AUTOCOMPLETE.email}
                    />
                    {errors.email && <span className="ems-fm-err">{errors.email}</span>}
                  </div>
                  <div className="ems-auth-field">
                    <label htmlFor="stu-pw">Password</label>
                    <div className="ems-auth-pw">
                      <input
                        id="stu-pw"
                        type={showPassword ? "text" : "password"}
                        className="ems-fm-input"
                        placeholder="Password from your email"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          setErrors((p) => ({ ...p, password: "" }));
                        }}
                        {...LOGIN_AUTOCOMPLETE.password}
                      />
                      <button
                        type="button"
                        className="ems-auth-pw-eye"
                        onClick={() => setShowPassword(!showPassword)}
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {errors.password && (
                      <span className="ems-fm-err">{errors.password}</span>
                    )}
                  </div>
                  <div className="ems-auth-meta">
                    <label className="ems-auth-remember">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                      />
                      Remember me
                    </label>
                    <Link
                      to={`/forgot-password?from=${encodeURIComponent(orgSlug || "")}${
                        email ? `&email=${encodeURIComponent(email)}` : ""
                      }`}
                      className="ems-auth-forgot"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <div className="ems-auth-actions">
                    <button type="submit" className="ems-btn ems-primary" disabled={loading}>
                      {loading ? <><SpinSVG /> Signing in…</> : "Sign In"}
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={otpSent ? submitOtp : (e) => { e.preventDefault(); sendOtp(); }}>
                  <div className="ems-auth-field">
                    <label htmlFor="stu-otp-email">Email Address</label>
                    <input
                      id="stu-otp-email"
                      type="text"
                      className="ems-fm-input"
                      placeholder="you@email.com"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setErrors((p) => ({ ...p, email: "" }));
                      }}
                    />
                    {errors.email && <span className="ems-fm-err">{errors.email}</span>}
                  </div>
                  {otpSent && (
                    <div className="ems-auth-field">
                      <label htmlFor="stu-otp">One-time code</label>
                      <input
                        id="stu-otp"
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        className="ems-fm-input"
                        placeholder="6-digit code"
                        value={otp}
                        onChange={(e) => {
                          setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
                          setErrors((p) => ({ ...p, otp: "" }));
                        }}
                      />
                      {errors.otp && <span className="ems-fm-err">{errors.otp}</span>}
                    </div>
                  )}
                  <div className="ems-auth-meta">
                    <label className="ems-auth-remember">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                      />
                      Remember me
                    </label>
                  </div>
                  <div className="ems-auth-actions">
                    <button type="submit" className="ems-btn ems-primary" disabled={loading}>
                      {loading ? (
                        <><SpinSVG /> Please wait…</>
                      ) : otpSent ? (
                        "Verify and sign in"
                      ) : (
                        "Send login code"
                      )}
                    </button>
                    {otpSent && (
                      <button
                        type="button"
                        className="ems-btn"
                        disabled={loading}
                        onClick={sendOtp}
                      >
                        Resend code
                      </button>
                    )}
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default StudentOrgLogin;
